import { Component, Input } from '@angular/core';
import { Contract, ContractPreview, FnolRequest, GeneralConfirmationModalResult, ParsedInvoiceData, TasksLabels, Treatment } from "../../../models";
import { MatDialog } from "@angular/material/dialog";
import {
  ContractAssignListModalComponent
} from "../../contract/contract-assign-list-modal/contract-assign-list-modal.component";
import { SidebarDetailsService } from "../../../services/sidebar-details.service";
import { ReserveModalComponent } from '../reserve-modal/reserve-modal.component';
import { ApiHttpService } from "../../../services/api-http.service";
import { checkSimpleClaim, contractIdFromContractNumber, calculatePetAgeAtTreatmentDate, calculateAdjustedAmount } from 'src/app/helpers/utility';
import { LoadingOverlayRef, LoadingService } from 'src/app/services/loading.service';
import { GeneralConfirmationModalComponent } from '../../general-confirmation-modal/general-confirmation-modal.component';


@Component({
  selector: 'CPF-invoice-assign-items',
  templateUrl: './invoice-assign-items.component.html',
  styleUrls: ['./invoice-assign-items.component.scss']
})
export class InvoiceAssignItemsComponent {
  @Input() fnolRequest: FnolRequest | undefined;

  contracts: Contract[] = [];

  @Input() set contractSelected(contractSelected: ContractPreview[]) {
    this.contracts = contractSelected.map(c => c.contracts).flat();
  }

  constructor(private dialog: MatDialog, private sidebarDetailsService: SidebarDetailsService, private apiService: ApiHttpService, private loadingService: LoadingService) {
  }

  findAssignedContract(fullContractNumber: string) {
    return this.contracts.find(c => c.full_contract_number === fullContractNumber);
  }

  assignContract(treatment: Treatment) {
    const dialogRef = this.dialog.open(ContractAssignListModalComponent, {
      data: { contracts: this.contracts },
      minWidth: '50%',
      width: '100%',
    });

    dialogRef.afterClosed().subscribe((result: Contract | null | 'empty') => {
      if (result) {
        if (result === 'empty') {
          treatment.contract = null;
          return;
        }

        delete result.claims;
        treatment.contract = { ...result };
      }
    });
  }

  setReserveWithConfirmation(treatment: Treatment) {
    if (this.fnolRequest?.initialReserveData) {
      const dialogRef = this.dialog.open(GeneralConfirmationModalComponent, {
        autoFocus: false,
        restoreFocus: false,
        data: {
          titleKey: 'general-confirmation-modal.modals.set-reserve-confirmation.title',
          messageKey: 'general-confirmation-modal.modals.set-reserve-confirmation.message',
          confirmButton: GeneralConfirmationModalResult.Confirm,
          cancelButton: GeneralConfirmationModalResult.Cancel,
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === GeneralConfirmationModalResult.Confirm) {
          this.setReserve(treatment);
        }
      });
    } else {
      this.setReserve(treatment);
    }
  }

  async setReserve(treatment: Treatment) {
    console.log('passing contract to reserve modal:', treatment.contract)
    // for debugging:
    // if (treatment.contract) {treatment.contract.birthdate = '2017-04-13';}
    // if (treatment.contract) {treatment.contract.deductible_percentage_until_9_years = 20;}
    
    // Calculate adjusted amount based on pet age and deductible percentage
    let adjustedAmount = this.fnolRequest?.parsedInvoiceData.calculationInformation?.totalInclVat || 0;
    
    if (treatment.contract?.birthdate) {
      // Calculate pet age
      const petAge = calculatePetAgeAtTreatmentDate(treatment.contract.birthdate, treatment.date);
      
      // Calculate adjusted amount
      adjustedAmount = calculateAdjustedAmount(
        adjustedAmount,
        petAge,
        treatment.contract.deductible_percentage_until_9_years,
        treatment.contract.deductible_percentage_after_9_years
      );
      
      // Determine which deductible type was used for logging
      const deductibleType = petAge >= 9 ? 'deductible_percentage_after_9_years' : 'deductible_percentage_until_9_years';
      const deductiblePercentage = petAge >= 9 ? treatment.contract.deductible_percentage_after_9_years : treatment.contract.deductible_percentage_until_9_years;
      
      console.log(`Pet birthday: ${new Date(treatment.contract.birthdate).toLocaleDateString()}, Treatment date: ${new Date(treatment.date).toLocaleDateString()}, Pet age at treatment: ${petAge} years, using ${deductibleType}: ${deductiblePercentage}%, adjusted amount: ${adjustedAmount}`);
    }
    
    const dialogRef = this.dialog.open(ReserveModalComponent, {
      data: {
        contractId: contractIdFromContractNumber(treatment.contract?.contract_number),
        fullContractNumber: treatment.contract?.full_contract_number,
        claimDate: treatment.date,
        amount: adjustedAmount,
        originalAmount: this.fnolRequest?.parsedInvoiceData.calculationInformation?.totalInclVat,
        courseOfevents: this.fnolRequest?.fnolAdditionalInformation || '',
        contract: treatment.contract
      },
      minWidth: '50%',
      width: '100%',
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result && this.fnolRequest) {
        const loadingRef: LoadingOverlayRef = this.loadingService.open();
        this.fnolRequest.initialReserveData = result
        
        if (checkSimpleClaim(this.fnolRequest)) {
          if (!this.fnolRequest.labels) {
            this.fnolRequest.labels = [TasksLabels.SIMPLE_CLAIM]
          } else {
            if (!this.fnolRequest.labels.includes(TasksLabels.SIMPLE_CLAIM)) {
              this.fnolRequest.labels.push(TasksLabels.SIMPLE_CLAIM)
            }
          }
        }

        try {
          const reserveResult = (await this.apiService.createInitialReserveMfv(this.fnolRequest))
          console.log('reserveResult', reserveResult)

          if (reserveResult.reserveDuplicateId) {
            console.log('got reserveResult.reserveDuplicateId from create reserve endpoint', reserveResult.reserveDuplicateId)
            if (!this.fnolRequest.reserveDuplicateIDs) {
              this.fnolRequest.reserveDuplicateIDs = []
            }
            if (!this.fnolRequest.reserveDuplicateIDs.includes(reserveResult.reserveDuplicateId)) {
            this.fnolRequest.reserveDuplicateIDs?.push(reserveResult.reserveDuplicateId)
            }
            if (!this.fnolRequest.labels) {
              this.fnolRequest.labels = []
            }
            if (!this.fnolRequest.labels.includes(TasksLabels.POTENTIAL_DUPLICATE)) {
              this.fnolRequest.labels.push(TasksLabels.POTENTIAL_DUPLICATE)
            }
          } else if (this.fnolRequest.initialReserveData && !this.fnolRequest.initialReserveData.claimID && !this.fnolRequest.initialReserveData.claimNumber) {
            this.fnolRequest.initialReserveData.claimID = reserveResult.claimID
            this.fnolRequest.initialReserveData.claimNumber = reserveResult.claimNumber
          }

          const res = await this.apiService.updateFnolRequest(this.fnolRequest);

          if (res) {
            const updatedRequests: FnolRequest[] = await this.apiService.markMultipleClaims(this.fnolRequest!.fnolEmail);
            let updatedLables = updatedRequests.find(request => request.id === this.fnolRequest?.id)?.labels;
            if (updatedLables) {
              this.fnolRequest.labels = updatedLables;
            }
          }

        } catch (e) {
          console.error(`Error while creating reserves or saving the FNOL request: ${e}`);
        } finally {
          loadingRef.close();
        }
      }
    })
  }

  async openContractDetailsSidebar(contract: Contract) {
    if (!contract) {
      return;
    }

    await this.sidebarDetailsService.setContractDetails(contract);
    this.sidebarDetailsService.open();
  }

  openContactInNewTab(contract: Contract) {
    window.open(this.buildContractLink(contract), '_blank');
  }

  buildContractLink(contract: Contract) {
    //https://webapp.simpego.ch/vorgaenge/vertraege/<SECOND_PART_OF_FULL_CONTRACT_NUMBER>
    return `https://webapp.simpego.ch/vorgaenge/vertraege/${contract.full_contract_number.split('-')[1]}`;
  }

}
